/**
 * Voorinspectie Detail Page (legacy-render)
 *
 * Form for performing pre-inspection of incoming wasserij items. Sinds plan
 * 2026-09-02 leeft laden/opslaan/navigatie in useVoorinspectieItem.ts;
 * VoorinspectieDetailPageWb.tsx is de Werkbank-render op dezelfde hook.
 * Captures: dimensions (nameten), voorinspectie multi-picklist, soort, merk, wasrapport notes.
 * Supports offline saving with sync queue.
 */

import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button, Badge } from '../components/ui';
import {
  MEDIA_EIGEN,
  VOORINSPECTIE_OPTIONS,
  RISICO_INDICATIE_VOORINSPECTIE,
  SOORT_OPTIONS,
  MERK_OPTIONS,
} from '../lib/constants';
import { CameraCapture } from '../components/CameraCapture';
import { MediaStrip } from '../components/MediaStrip';
import type { VoorinspectieItem, VormType } from '../lib/salesforce';
import { RisicoMeldDialog } from '../components/RisicoMeldDialog';
import { ReparatieIntentCard } from '../components/ReparatieIntentCard';
import { BerichtenInline } from '../components/berichten/BerichtenInline';
import { KlachtBanner } from '../components/KlachtBanner';
import { KlantEigenItemCard } from '../components/KlantEigenItemCard';
import { isWerkbank } from '../lib/uiFlag';
import { useVoorinspectieItem } from './useVoorinspectieItem';
import { VoorinspectieDetailPageWb } from './VoorinspectieDetailPageWb';

// Risico status card — boven aan de detail-pagina, voor de Item info card.
// Toont badge + knop voor melden + banner per state. Sinds het versimpelde
// proces (v2) beoordeelt kantoor eerst of het echt een risico is (Gemeld ->
// Bevestigd) en verstuurt daarna pas de mail (-> Wacht_klant).
function RisicoStatusCard({
  item,
  onMeldClick,
}: {
  item: VoorinspectieItem;
  onMeldClick: () => void;
}) {
  const status = item.risicoStatus;
  const types = (item.risicoTypes || '').split(';').filter(Boolean);

  if (status === 'Geen' || !status) {
    return (
      <Card>
        <CardContent className="py-md flex items-center justify-between gap-md">
          <div>
            <p className="text-sm font-medium text-kleda-text-main">
              Zie je iets bijzonders?
            </p>
            <p className="text-xs text-kleda-text-muted mt-xs">
              Schade, kleurverlies, krimp-risico: vink het aan en rond je
              voorinspectie gewoon af. Kantoor pakt het op.
            </p>
          </div>
          <Button variant="outline" size="md" onClick={onMeldClick}>
            🚨 Risico melden
          </Button>
        </CardContent>
      </Card>
    );
  }

  const badge =
    status === 'Gemeld'
      ? { color: 'bg-yellow-100 text-yellow-800', label: '🟡 Gemeld, wacht op kantoor' }
      : status === 'Bevestigd'
      ? { color: 'bg-orange-100 text-orange-800', label: '🟠 Bevestigd door kantoor' }
      : status === 'Wacht_klant'
      ? { color: 'bg-yellow-100 text-yellow-800', label: '🟡 Wacht op klant' }
      : status === 'Akkoord'
      ? { color: 'bg-green-100 text-green-800', label: '🟢 Klant gaf akkoord' }
      : status === 'Niet_Akkoord'
      ? { color: 'bg-red-100 text-red-800', label: '🔴 Klant zegt nee' }
      : { color: 'bg-blue-100 text-blue-800', label: 'Alternatief gekozen' };

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <div className="flex items-center gap-sm">
            <span>Risico-melding</span>
            <Badge variant="default" className={`text-xs ${badge.color}`}>
              {badge.label}
            </Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-sm">
        {types.length > 0 && (
          <div className="flex flex-wrap gap-xs">
            {types.map((t) => (
              <Badge key={t} variant="default" className="text-xs">
                {t}
              </Badge>
            ))}
          </div>
        )}
        {item.risicoToelichting && (
          <p className="text-sm text-kleda-text-main bg-kleda-bg-clean p-sm rounded-kleda-sm">
            "{item.risicoToelichting}"
          </p>
        )}

        {status === 'Niet_Akkoord' && (
          <div className="bg-red-50 border-l-4 border-red-500 p-sm rounded-kleda-sm">
            <p className="text-sm font-medium text-red-800">Klant zegt nee — actie nodig</p>
            <p className="text-xs text-red-700 mt-xs">
              Bel de klant voor een alternatief, of laat office het kleed annuleren.
            </p>
            {item.risicoKlantReactie && (
              <p className="text-xs text-red-700 mt-sm italic">
                Reactie klant: "{item.risicoKlantReactie}"
              </p>
            )}
            <div className="mt-sm">
              <Button variant="outline" size="sm" onClick={onMeldClick}>
                Opnieuw melden
              </Button>
            </div>
          </div>
        )}

        {status === 'Akkoord' && item.risicoKlantReactie && (
          <p className="text-xs text-kleda-text-muted italic">
            Klant: "{item.risicoKlantReactie}"
          </p>
        )}

        {status === 'Wacht_klant' && item.risicoGemeldOp && (
          <div className="text-xs text-kleda-text-muted space-y-xs">
            <p>
              Kantoor heeft de klant gemaild
              {(() => {
                const dagen = Math.floor(
                  (Date.now() - new Date(item.risicoGemeldOp).getTime()) / (1000 * 60 * 60 * 24)
                );
                return dagen > 0 ? ` (wacht ${dagen}d)` : '';
              })()}
              . Zodra de klant antwoordt, verwerkt kantoor het en gaat het kleed
              verder.
            </p>
          </div>
        )}

        {status === 'Gemeld' && (
          <p className="text-xs text-kleda-text-muted">
            Kantoor beoordeelt je melding. Rond je voorinspectie gerust af.
          </p>
        )}

        {status === 'Bevestigd' && (
          <p className="text-xs text-kleda-text-muted">
            Kantoor bevestigde het risico en mailt de klant.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Shape options for dimension input
const VORM_OPTIONS: { value: VormType; label: string; icon: React.ReactNode }[] = [
  {
    value: 'Rechthoek',
    label: 'Rechthoek',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="8" width="24" height="16" rx="1" />
      </svg>
    ),
  },
  {
    value: 'Rond',
    label: 'Rond',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="16" cy="16" r="12" />
      </svg>
    ),
  },
  {
    value: 'Anders',
    label: 'Anders',
    icon: (
      <svg className="w-8 h-8" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M16 4 L28 12 L24 28 L8 28 L4 12 Z" />
      </svg>
    ),
  },
];

export function VoorinspectieDetailPage() {
  if (isWerkbank()) return <VoorinspectieDetailPageWb />;
  return <VoorinspectieDetailPageLegacy />;
}

function VoorinspectieDetailPageLegacy() {
  const vi = useVoorinspectieItem();
  const {
    itemId,
    item, allItems, isLoading, error, loadItem,
    vorm, lengthCm, setLengthCm, widthCm, setWidthCm, diameterCm, setDiameterCm, aantal, setAantal,
    handleVormChange, calculatedOppervlakte, existingOppervlakte,
    selectedVoorinspectie, toggleVoorinspectie,
    soort, setSoort, merk, setMerk,
    voorinspectieOpmerkingen, setVoorinspectieOpmerkingen, wasrapport, setWasrapport,
    isSaving, handleSave,
    risicoDialogOpen, setRisicoDialogOpen, risicoAfrondPrompt, setRisicoAfrondPrompt, markeerGemeld,
    showCamera, setShowCamera, mediaRefresh, setMediaRefresh,
    nextItem,
  } = vi;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-kleda-primary border-t-transparent rounded-full animate-spin mx-auto mb-md" />
          <p className="text-kleda-text-muted">Item laden...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !item) {
    return (
      <div className="space-y-lg">
        <Link
          to="/voorinspectie"
          className="inline-flex items-center gap-xs text-kleda-primary hover:underline"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Terug naar lijst
        </Link>

        <Card>
          <CardContent>
            <div className="text-center py-xl">
              <svg
                className="w-12 h-12 text-kleda-text-muted mx-auto mb-md"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
              <p className="text-kleda-text-muted">{error || 'Item niet gevonden'}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-lg pb-xl">
      {/* Back button */}
      <Link
        to="/voorinspectie"
        className="inline-flex items-center gap-xs text-kleda-primary hover:underline"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Terug naar lijst
      </Link>

      {/* Header */}
      <div>
        <h2 className="font-heading text-xl font-bold text-kleda-text-main">Voorinspectie</h2>
        <p className="text-kleda-text-muted">
          {item.productName} • {item.itemName}
        </p>
      </div>

      {/* Klacht-markering — zichtbaar door de hele wasserij-keten */}
      {item.isKlacht && <KlachtBanner reden={item.klachtReden} />}

      {/* Pre-pickup reparatie-intent (klantverzoek vóór ophaal) */}
      <ReparatieIntentCard item={item} onChanged={() => loadItem({ silent: true })} />

      {/* Berichten — communicatielijn met kantoor over dit item */}
      <BerichtenInline
        scope="wi"
        werkOrderId={item.werkOrderId}
        werkOrderName={item.werkOrderName}
        wasserijItemId={item.itemId}
        wasserijItemName={item.itemName}
      />

      {/* Risico-flow card */}
      <RisicoStatusCard
        item={item}
        onMeldClick={() => setRisicoDialogOpen(true)}
      />

      {/* Item info */}
      <Card>
        <CardHeader>
          <CardTitle>Objectgegevens</CardTitle>
        </CardHeader>
        <CardContent className="space-y-md">
          <div className="grid grid-cols-2 gap-md text-sm">
            <div>
              <span className="text-kleda-text-muted">Nummer:</span>
              <span className="ml-xs font-medium text-kleda-text-main">{item.itemName}</span>
            </div>
            <div>
              <span className="text-kleda-text-muted">Product:</span>
              <span className="ml-xs font-medium text-kleda-text-main">{item.productName}</span>
            </div>
            {item.lengthCm && item.widthCm && (
              <div>
                <span className="text-kleda-text-muted">Afmeting:</span>
                <span className="ml-xs font-medium text-kleda-text-main">
                  {item.lengthCm} x {item.widthCm} cm
                </span>
              </div>
            )}
            {item.diameterCm && (
              <div>
                <span className="text-kleda-text-muted">Diameter:</span>
                <span className="ml-xs font-medium text-kleda-text-main">
                  {item.diameterCm} cm
                </span>
              </div>
            )}
            {existingOppervlakte && (
              <div>
                <span className="text-kleda-text-muted">Oppervlakte:</span>
                <span className="ml-xs font-medium text-kleda-text-main">
                  {existingOppervlakte.toFixed(2)} m²
                </span>
              </div>
            )}
            {item.werkOrderName && (
              <div className="col-span-2">
                <span className="text-kleda-text-muted">Werk order:</span>
                <span className="ml-xs font-medium text-kleda-text-main">{item.werkOrderName}</span>
              </div>
            )}
          </div>

          {/* Existing Wasrapport */}
          {item.wasrapport && (
            <div className="pt-md border-t border-gray-200">
              <p className="text-sm font-medium text-kleda-text-main mb-xs">Bestaand wasrapport:</p>
              <p className="text-sm text-kleda-text-muted whitespace-pre-wrap bg-kleda-bg-clean p-sm rounded-kleda-sm">
                {item.wasrapport}
              </p>
            </div>
          )}

          {/* Secondary Items (Pakket + Extra's) */}
          {item.secondaryItems && item.secondaryItems.length > 0 && (
            <div className="pt-md border-t border-gray-200">
              <p className="text-sm font-medium text-kleda-text-main mb-sm">
                Pakket & Extra's
              </p>
              <div className="flex flex-wrap gap-sm">
                {item.secondaryItems.map((secItem) => (
                  <Badge
                    key={secItem.itemId}
                    variant={secItem.category === 'Package' ? 'info' : 'purple'}
                    size="md"
                  >
                    {secItem.productName}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Los klant-eigen item (ondertapijt/spanbanden/…) op order-niveau taggen */}
      <KlantEigenItemCard item={item} onAdded={() => loadItem({ silent: true })} />

      {/* Nameten - Dimension Measurement */}
      <Card>
        <CardHeader>
          <CardTitle>Nameten</CardTitle>
        </CardHeader>
        <CardContent className="space-y-lg">
          {/* Shape Selector */}
          <div>
            <p className="text-sm text-kleda-text-muted mb-md">Selecteer de vorm:</p>
            <div className="grid grid-cols-3 gap-sm">
              {VORM_OPTIONS.map((option) => {
                const isSelected = vorm === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleVormChange(option.value)}
                    className={`flex flex-col items-center justify-center p-md rounded-kleda-md border-2 transition-all ${
                      isSelected
                        ? 'border-kleda-primary bg-kleda-primary/5 text-kleda-primary'
                        : 'border-gray-200 text-kleda-text-muted hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={isSelected ? 'text-kleda-primary' : 'text-gray-400'}>
                      {option.icon}
                    </div>
                    <span className={`text-sm font-medium mt-xs ${isSelected ? 'text-kleda-primary' : 'text-kleda-text-main'}`}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Conditional Dimension Inputs */}
          {vorm === 'Rechthoek' && (
            <div className="space-y-md animate-fadeIn">
              <div className="grid grid-cols-2 gap-md">
                <div>
                  <label className="block text-sm font-medium text-kleda-text-main mb-xs">
                    Lengte (cm)
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={lengthCm}
                    onChange={(e) => setLengthCm(e.target.value)}
                    placeholder="bijv. 300"
                    className="kleda-input text-lg font-medium text-center"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-kleda-text-main mb-xs">
                    Breedte (cm)
                  </label>
                  <input
                    type="number"
                    inputMode="decimal"
                    value={widthCm}
                    onChange={(e) => setWidthCm(e.target.value)}
                    placeholder="bijv. 200"
                    className="kleda-input text-lg font-medium text-center"
                  />
                </div>
              </div>
              {/* Live Area Calculation */}
              {calculatedOppervlakte && (
                <div className="bg-kleda-bg-clean rounded-kleda-md p-md text-center">
                  <p className="text-sm text-kleda-text-muted">Oppervlakte</p>
                  <p className="text-2xl font-bold text-kleda-primary">
                    {calculatedOppervlakte.toFixed(2)} m²
                  </p>
                </div>
              )}
            </div>
          )}

          {vorm === 'Rond' && (
            <div className="space-y-md animate-fadeIn">
              <div>
                <label className="block text-sm font-medium text-kleda-text-main mb-xs">
                  Diameter (cm)
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  value={diameterCm}
                  onChange={(e) => setDiameterCm(e.target.value)}
                  placeholder="bijv. 250"
                  className="kleda-input text-lg font-medium text-center"
                />
              </div>
              {/* Live Area Calculation */}
              {calculatedOppervlakte && (
                <div className="bg-kleda-bg-clean rounded-kleda-md p-md text-center">
                  <p className="text-sm text-kleda-text-muted">Oppervlakte</p>
                  <p className="text-2xl font-bold text-kleda-primary">
                    {calculatedOppervlakte.toFixed(2)} m²
                  </p>
                </div>
              )}
            </div>
          )}

          {vorm === 'Anders' && (
            <div className="space-y-md animate-fadeIn">
              <div>
                <label className="block text-sm font-medium text-kleda-text-main mb-xs">
                  Vul hier de oppervlakte (ongeveer) in
                </label>
                <div className="flex items-center gap-sm">
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    value={aantal}
                    onChange={(e) => setAantal(e.target.value)}
                    min="0.01"
                    placeholder="0.00"
                    className="kleda-input flex-1 text-lg"
                  />
                  <span className="text-lg font-medium text-kleda-text-muted">m²</span>
                </div>
              </div>
              <p className="text-sm text-kleda-text-muted text-center">
                Voor objecten met een onregelmatige vorm. Vul een schatting van de oppervlakte in.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Voorinspectie options (multi-select chips) */}
      <Card>
        <CardHeader>
          <CardTitle>Voorinspectie</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-kleda-text-muted mb-md">
            Selecteer alle van toepassing zijnde punten:
          </p>
          <div className="flex flex-wrap gap-sm">
            {VOORINSPECTIE_OPTIONS.map((option) => {
              const isSelected = selectedVoorinspectie.includes(option.value);
              return (
                <button
                  key={option.value}
                  onClick={() => toggleVoorinspectie(option.value)}
                  className={`px-md py-sm rounded-full text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-kleda-primary text-white'
                      : 'bg-kleda-bg-clean text-kleda-text-main hover:bg-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {selectedVoorinspectie.length > 0 && (
            <p className="text-xs text-kleda-text-muted mt-md">
              {selectedVoorinspectie.length} geselecteerd
            </p>
          )}
        </CardContent>
      </Card>

      {/* Soort dropdown */}
      <Card>
        <CardHeader>
          <CardTitle>Soort</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={soort}
            onChange={(e) => setSoort(e.target.value)}
            className="kleda-input"
          >
            <option value="">-- Selecteer soort --</option>
            {SOORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Merk dropdown */}
      <Card>
        <CardHeader>
          <CardTitle>Merk</CardTitle>
        </CardHeader>
        <CardContent>
          <select
            value={merk}
            onChange={(e) => setMerk(e.target.value)}
            className="kleda-input"
          >
            <option value="">-- Selecteer merk --</option>
            {MERK_OPTIONS.map((option) => (
              <option key={option.value || 'none'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {/* Voorinspectie Opmerkingen (customer-facing) */}
      <Card>
        <CardHeader>
          <CardTitle>Opmerkingen voorinspectie</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-kleda-text-muted mb-sm">
            Deze opmerkingen worden naar de klant gestuurd in het voorinspectierapport.
          </p>
          <textarea
            value={voorinspectieOpmerkingen}
            onChange={(e) => setVoorinspectieOpmerkingen(e.target.value)}
            placeholder="Opmerkingen voor klant..."
            rows={4}
            className="kleda-input resize-none"
          />
        </CardContent>
      </Card>

      {/* Wasrapport notes (internal) */}
      <Card>
        <CardHeader>
          <CardTitle>Toevoegen aan wasrapport (intern)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-kleda-text-muted mb-sm">
            Deze notities zijn alleen intern zichtbaar.
          </p>
          <textarea
            value={wasrapport}
            onChange={(e) => setWasrapport(e.target.value)}
            placeholder="Interne bijzonderheden..."
            rows={4}
            className="kleda-input resize-none"
          />
        </CardContent>
      </Card>

      {/* Photos - CompanyCam */}
      <Card>
        <CardHeader>
          <CardTitle>Foto's</CardTitle>
        </CardHeader>
        <CardContent className="space-y-sm">
          {/* Main button - eigen camera (MEDIA_EIGEN) of CompanyCam-deeplink */}
          <button
            onClick={() => {
              if (MEDIA_EIGEN) {
                setShowCamera(true);
              } else if (item.companyCamProjectId) {
                // Deep link directly to camera in this project
                window.location.href = `ccam://camera/${item.companyCamProjectId}`;
              } else {
                // Fallback: open search so user can find the project by name
                window.location.href = 'ccam://search';
              }
            }}
            className="flex items-center justify-center w-full px-md py-md rounded-kleda-md bg-kleda-primary text-white font-medium hover:bg-kleda-primary/90 transition-colors"
          >
            <svg className="w-5 h-5 mr-sm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {MEDIA_EIGEN || item.companyCamProjectId ? "Maak foto's" : "Open CompanyCam"}
          </button>

          {/* Info text */}
          <div className="text-center text-xs text-kleda-text-muted">
            {MEDIA_EIGEN || item.companyCamProjectId ? (
              <>
                <p>Foto's worden automatisch gekoppeld aan <strong>{item.itemName}</strong></p>
                {/* Social-Reels zetten het kleed recht van boven; dat lukt alleen als het
                    hele kleed in beeld staat (plan 2026-09-01, render v3.1). */}
                <p className="mt-xs">Eén foto van iets verder weg met het <strong>hele kleed in beeld</strong>, franjes erbij. Van dichtbij mag daarna.</p>
              </>
            ) : (
              <p>Zoek project: <strong>{item.itemName}</strong></p>
            )}
          </div>

          {/* Eigen media van dit item (media-laag fase 2) */}
          {MEDIA_EIGEN && <MediaStrip wiSfId={item.itemId} refreshKey={mediaRefresh} />}

          {/* Secondary button - view existing photos (CompanyCam-pad) */}
          {!MEDIA_EIGEN && item.companyCamProjectId && (
            <button
              onClick={() => {
                window.location.href = `ccam://projects/${item.companyCamProjectId}`;
              }}
              className="flex items-center justify-center w-full px-md py-sm rounded-kleda-md border border-kleda-border text-kleda-text-main font-medium hover:bg-kleda-bg-clean transition-colors"
            >
              <svg className="w-4 h-4 mr-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              Bekijk foto's
            </button>
          )}
        </CardContent>
      </Card>

      {/* Action buttons */}
      <Card>
        <CardContent className="space-y-sm">
          <Button fullWidth size="lg" onClick={() => handleSave(false)} isLoading={isSaving}>
            Opslaan
          </Button>

          {nextItem && (
            <Button
              variant="outline"
              fullWidth
              size="lg"
              onClick={() => handleSave(true)}
              disabled={isSaving}
            >
              <span>Opslaan & Volgende</span>
              <svg className="w-4 h-4 ml-xs" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Queue indicator */}
      {allItems.length > 1 && (
        <p className="text-center text-xs text-kleda-text-muted">
          Item {allItems.findIndex((i) => i.itemId === itemId) + 1} van {allItems.length}
        </p>
      )}

      {/* Media-laag camera (MEDIA_EIGEN) */}
      {showCamera && (
        <CameraCapture
          fase="voorinspectie"
          doel={{ wiSfId: item.itemId }}
          subtitle={item.itemName}
          onClose={(aantal) => {
            setShowCamera(false);
            if (aantal > 0) setMediaRefresh((k) => k + 1);
          }}
        />
      )}

      {/* Afrond-guard: schade aangevinkt, geen melding — eerst kiezen */}
      {risicoAfrondPrompt && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50">
          {/* Bewust géén backdrop-close: dit is precies het beslismoment */}
          <div className="relative w-full max-w-lg bg-white rounded-t-2xl animate-slide-up p-lg space-y-md">
            <div>
              <h2 className="text-lg font-bold text-kleda-text-main">
                🚨 Schade aangevinkt, geen risico gemeld
              </h2>
              <p className="text-sm text-kleda-text-muted mt-xs">
                Je vinkte aan:{' '}
                <strong>
                  {selectedVoorinspectie
                    .filter((v) => RISICO_INDICATIE_VOORINSPECTIE.has(v))
                    .map(
                      (v) => VOORINSPECTIE_OPTIONS.find((o) => o.value === v)?.label ?? v
                    )
                    .join(', ')}
                </strong>
                . Aanvinken is geen melding: zonder risico-melding loopt het kleed
                gewoon door naar reinigen, zonder akkoord van de klant.
              </p>
            </div>
            <div className="space-y-sm">
              <Button
                fullWidth
                size="lg"
                onClick={() => {
                  setRisicoAfrondPrompt(null);
                  setRisicoDialogOpen(true);
                }}
              >
                🚨 Eerst risico melden
              </Button>
              <Button
                fullWidth
                variant="outline"
                size="lg"
                onClick={() => {
                  const prompt = risicoAfrondPrompt;
                  setRisicoAfrondPrompt(null);
                  handleSave(prompt.goToNext, { negeerRisicoIndicatie: true });
                }}
              >
                Afronden zonder melding
              </Button>
              <Button
                fullWidth
                variant="ghost"
                size="md"
                onClick={() => setRisicoAfrondPrompt(null)}
              >
                Terug naar formulier
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Risico meld dialog */}
      {risicoDialogOpen && (
        <RisicoMeldDialog
          itemId={item.itemId}
          itemName={item.itemName}
          companyCamProjectId={item.companyCamProjectId}
          onClose={() => setRisicoDialogOpen(false)}
          onSubmitted={markeerGemeld}
        />
      )}
    </div>
  );
}
